import { Badge, type BadgeTone } from '@ui';
import { useTranslation } from 'react-i18next';
import type { InvoiceStatus, PaymentMethod, SaleStatus } from '../api';

const STATUS_TONE: Record<SaleStatus, BadgeTone> = {
  draft: 'neutral',
  ordered: 'info',
  delivered: 'success',
  cancelled: 'neutral',
};

export function SaleStatusBadge({ status }: { status: SaleStatus }) {
  const { t } = useTranslation();
  return (
    <Badge tone={STATUS_TONE[status]} size="sm">
      {t(`sales.status.${status}`)}
    </Badge>
  );
}

/** Paid (with method) or open; nothing for drafts and cancelled sales. */
export function PaymentBadge({
  status,
  paid,
  method,
}: {
  status: SaleStatus;
  paid: boolean;
  method?: PaymentMethod | null | undefined;
}) {
  const { t } = useTranslation();
  if (status === 'draft' || status === 'cancelled') return null;
  return paid ? (
    <Badge tone="success" size="sm">
      {method ? t(`sales.method.${method}`) : t('sales.payment.paid')}
    </Badge>
  ) : (
    <Badge tone="warning" size="sm">
      {t('sales.payment.open')}
    </Badge>
  );
}

/**
 * The one status of a sale in lists that deserves attention (SAL-002):
 * draft, ordered, cancelled or delivered but unpaid. A delivered and paid
 * sale is the normal case and gets no badge (docs/ux-richtlinien.md §2).
 */
export function SaleStateBadge({ status, paid }: { status: SaleStatus; paid: boolean }) {
  const { t } = useTranslation();
  if (status !== 'delivered') return <SaleStatusBadge status={status} />;
  if (paid) return null;
  return (
    <Badge tone="warning" size="sm">
      {t('sales.payment.open')}
    </Badge>
  );
}

export function InvoiceBadge({
  status,
  paid,
  overdue,
  cancellation,
}: {
  status: InvoiceStatus;
  paid: boolean;
  overdue: boolean;
  cancellation: boolean;
}) {
  const { t } = useTranslation();
  if (cancellation) {
    return (
      <Badge tone="neutral" size="sm">
        {t('sales.invoice.badge.cancellation')}
      </Badge>
    );
  }
  if (status === 'draft') {
    return (
      <Badge tone="neutral" size="sm">
        {t('sales.invoice.badge.draft')}
      </Badge>
    );
  }
  if (status === 'cancelled') {
    return (
      <Badge tone="neutral" size="sm">
        {t('sales.invoice.badge.cancelled')}
      </Badge>
    );
  }
  if (paid) {
    return (
      <Badge tone="success" size="sm">
        {t('sales.invoice.badge.paid')}
      </Badge>
    );
  }
  return (
    <Badge tone={overdue ? 'danger' : 'warning'} size="sm">
      {overdue ? t('sales.invoice.badge.overdue') : t('sales.invoice.badge.open')}
    </Badge>
  );
}
